<template>
  <div ref="container" class="pic-distribuzione">
    
    <!-- Titolo -->
    <div class="section-title">
      DISTRIBUZIONE SPESE
    </div>

    <!-- Separatore -->
    <div class="section-divider"></div>

    <!-- Card unica grafici -->
    <div class="chart-section">
      <div class="chart-row">

        <!-- Grafico categorie -->
        <div class="chart-box" :class="{ expanded: expandedCharts }">
          <div class="chart-title">
            Per categoria di spesa
          </div>

          <!-- Area grafico con altezza indipendente dalla legenda -->
          <div class="chart-canvas-wrapper">
            <canvas ref="categoriaChart"></canvas>
          </div>

          <!-- Legenda esterna al canvas per non ridurre il grafico -->
          <div
            v-if="distribuzione.categoria"
            class="chart-legend"
          >
            <div
              v-for="(label, index) in distribuzione.categoria.labels"
              :key="index"
              class="legend-item"
            >
              <span
                class="legend-color"
                :style="{
                  backgroundColor: coloreIndice(index)
                }"
              ></span>

              <span class="legend-label">
                {{ label }}
              </span>
            </div>
          </div>
        </div>

        <!-- Grafico dipendenti -->
        <div class="chart-box" :class="{ expanded: expandedCharts }">
          <div class="chart-title">
            Per dipendente (Top 8)
          </div>

          <!-- Area grafico con la stessa altezza del grafico categorie -->
          <div class="chart-canvas-wrapper">
            <canvas ref="dipendenteChart"></canvas>
          </div>

          <!-- Legenda esterna al canvas per non ridurre il grafico -->
          <div
            v-if="distribuzione.dipendente"
            class="chart-legend"
          >
            <div
              v-for="(label, index) in getTopDipendenti(distribuzione.dipendente).labels"
              :key="index"
              class="legend-item"
            >
              <span
                class="legend-color"
                :style="{
                  backgroundColor: coloreIndice(index)
                }"
              ></span>

              <span class="legend-label">
                {{ label }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import Chart from "chart.js";

export default {

  name: "PICDistribuzione",
  props: {
    distribuzione: {
      type: Object,
      default: () => ({})
    }
  },

  data() {

    return {
      chartCategoria: null,
      chartDipendente: null,
      resizeObserver: null,
      resizeTimeout: null,
      resizeFrame: null,
      containerWidth: 0,
      expandedCharts: false,

      // Palette condivisa dai grafici della Dashboard.
      colori: [
        "#17365D",
        "#1F4E79",
        "#2E75B6",
        "#3D85C6",
        "#4A90C2",
        "#5B9BD5",
        "#6FA8DC",
        "#8DB9E2",
        "#A9CBE8",
        "#C6DDF0"
      ]
    };
  },

 mounted() {

    this.createCharts();
    this.$nextTick(() => {

      if (!window.ResizeObserver)
        return;

      this.resizeObserver = new ResizeObserver(entries => {

        const width = Math.round(entries[0].contentRect.width);

        // ignora variazioni minime
        if (Math.abs(width - this.containerWidth) < 30)
          return;

        this.containerWidth = width;
        this.expandedCharts = width > 1050;
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {

          this.resizeCharts();

        }, 80);
      });

      this.resizeObserver.observe(this.$refs.container);

    });
  },

  watch: {
    distribuzione: {
      deep: true,
      handler() {
        this.createCharts();
      }
    }
  },

  beforeDestroy() {

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    clearTimeout(this.resizeTimeout);
    cancelAnimationFrame(this.resizeFrame);
    this.destroyCharts();
  },

  methods: {

    createCharts() {
      this.destroyCharts();
      this.$nextTick(() => {
        this.createChart(
          this.$refs.categoriaChart,
          this.distribuzione.categoria
        );

        this.createChart(
          this.$refs.dipendenteChart,
          this.getTopDipendenti(this.distribuzione.dipendente)
        );
      });
    },

    createChart(canvas, data) {

      if (!canvas || !data) {
        return;
      }

      if (!data.values || !data.labels) {
        return;
      }

      if (!data.values.length) {
        return;
      }

      const vm = this;

      const chart = new Chart(canvas, {
        type: "doughnut",
        data: {
          labels: data.labels,
          datasets: [
            {
              data: data.values,
              backgroundColor: this.colori,

              /*
               * Chart.js 2.9.4 non dispone della proprietà spacing.
               * Il bordo bianco viene utilizzato per creare
               * visivamente la separazione tra le fette.
               */
              borderWidth: 4,
              borderColor: "#ffffff"
            }
          ]
        },

        options: {
          responsive: true,
          maintainAspectRatio: false,

          /*
           * La legenda nativa viene disabilitata perché occuperebbe
           * spazio all'interno del canvas riducendo la dimensione
           * del grafico. La legenda viene visualizzata separatamente.
           */
          legend: {
            display: false
          },

          tooltips: {
            titleFontSize: 14,
            titleFontStyle: "bold",
            bodyFontSize: 14,
            bodyFontStyle: "bold",
            xPadding: 12,
            yPadding: 10,

            callbacks: {
              label(tooltipItem, chartData) {

                const label =
                  chartData.labels[tooltipItem.index];

                const value =
                  chartData.datasets[0].data[tooltipItem.index];

                 const nome =
                   vm.containerWidth < 800 && label.length > 12
                     ? label.slice(0, 12) + "..."
                     : label;

                return (
                  nome + ": € " +
                  vm.formatEuro(value)
                );
              }
            }
          }
        }
      });

      if (canvas === this.$refs.categoriaChart) {
        this.chartCategoria = chart;
      }

      if (canvas === this.$refs.dipendenteChart) {
        this.chartDipendente = chart;
      }
    },

    /*
     * Restituisce il colore corrispondente alla posizione
     * dell'elemento nella palette dei grafici.
     */
    coloreIndice(index) {

      return this.colori[
        index % this.colori.length
      ];
    },

    destroyCharts() {
      if (this.chartCategoria) {
        this.chartCategoria.destroy();
        this.chartCategoria = null;
      }

      if (this.chartDipendente) {
        this.chartDipendente.destroy();
        this.chartDipendente = null;
      }
    },

    resizeCharts() {

      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        if (this.chartCategoria) {
          this.chartCategoria.resize();
        }
        if (this.chartDipendente) {
          this.chartDipendente.resize();
        }
      });
    },

    getTopDipendenti(data) {

      if (!data || !data.labels || !data.values) {
        return data;
      }

       const items = data.labels.map((label, index) => ({
        label,
        value: data.values[index]
      }));

      // ordina dal più alto al più basso
      items.sort((a, b) => b.value - a.value);

      // se sono già 8 o meno
      if (items.length <= 8) {
        return {
          labels: items.map(i => i.label),
          values: items.map(i => i.value)
        };
      }

      const top = items.slice(0, 8);

      const altri = items
        .slice(8)
        .reduce((tot, item) => tot + item.value, 0);

      return {
        labels: [
          ...top.map(i => i.label),
          "Altri"
        ],
        values: [
          ...top.map(i => i.value),
          altri
        ]
      };
    },
    // Formatta l'importo in euro con separatore delle migliaia e due decimali
    formatEuro(value) {

      const valore = Number(value || 0);

      if (valore === 0) {
        return "—";
      }

      const numero = new Intl.NumberFormat(
        "it-IT",
        {
          useGrouping: true,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      ).format(valore);

      return `€ ${numero}`;
    }
  }
};

</script>

<style scoped>

.pic-distribuzione {
  width:100%;
}

.section-title {
  font-size:16px;
  font-weight:600;
  color:#373F66;
  margin-bottom:4px;
}

.section-divider {
  height:2px;
  background:#373F66;
  margin-bottom:10px;
}

.chart-section {
  background:#ffffff;
  border:1px solid #e5e5e5;
  border-radius:8px;
  padding:10px 12px 20px;
}

.chart-row {
  display:flex;
  gap:16px;
  align-items:flex-start;
}

.chart-box {
  flex:1;
  min-width:0;
  position:relative;
}

/* I due canvas hanno sempre la stessa altezza.
   La legenda viene visualizzata sotto e non modifica la dimensione della ciambella. */
.chart-canvas-wrapper {
  width:100%;
  height:240px;
  position:relative;
  transition:height .25s ease;
}

.chart-box.expanded .chart-canvas-wrapper {
  height:310px;
}

.chart-title {
  font-size:14px;
  font-weight:700;
  color:#666;
  text-align:center;
  margin-bottom:6px;
}

/* LEGENDA */

.chart-legend {
  display:flex;
  flex-wrap:wrap;
  justify-content:center;
  align-items:center;
  gap:10px 18px;
  margin-top:12px;
  padding:0 6px;
}

.legend-item {
  display:flex;
  align-items:center;
  min-width:0;
  font-size:13px;
  font-weight:700;
  color:#666;
}

.legend-color {
  flex:0 0 13px;
  width:13px;
  height:13px;
  margin-right:6px;
}

.legend-label {
  line-height:1.2;
}

canvas {
  display:block;
}

@media (max-width:768px){

    .chart-row {
        flex-direction:column;
    }

    .chart-box{
        width:100%;
    }

    .chart-canvas-wrapper {
        height:220px;
    }

    .chart-box.expanded .chart-canvas-wrapper {
        height:220px;
    }

    .chart-legend {
        gap:8px 14px;
    }

    .legend-item {
        font-size:12px;
    }
}

</style>